import {
  EmptyState,
  FlexBox,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { Waypoint } from 'react-waypoint';

import { AiLoadingState } from 'components/aiLoadingState';

import {
  EnhancedTemplate,
  useQuickReplyData,
} from '../../util/useQuickReplyData';

import { TemplateItem } from './components/templateItem/templateItem';
import styles from './templateList.module.scss';

interface TemplateListProps {
  sectionTitle: string;
  templates: readonly EnhancedTemplate[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  nextPage?: () => void;
  errorMessage: string;
  emptyMessage?: string;
  emptyDescription?: string;
  loadingMessage?: string;
  loadingHeight?: string;
  toggleFavorite: ReturnType<typeof useQuickReplyData>['toggleFavorite'];
  searchTerm?: string;
  locale: string;
  onApplyTemplate: () => void;
  showAutomation?: boolean;
}

const LoadingBox = ({
  loadingHeight,
  loadingMessage,
}: Pick<TemplateListProps, 'loadingMessage' | 'loadingHeight'>) => (
  <FlexBox
    height={loadingHeight}
    display="flex"
    alignItems="center"
    justifyContent="center"
    className={styles.loadingState}
  >
    {(loadingMessage && <AiLoadingState label={loadingMessage} />) || (
      <Spinner size={32} />
    )}
  </FlexBox>
);

export const TemplateList = ({
  sectionTitle,
  templates,
  isLoading,
  isError,
  hasNextPage,
  errorMessage,
  emptyMessage,
  emptyDescription,
  loadingMessage,
  loadingHeight,
  nextPage,
  toggleFavorite,
  searchTerm,
  locale,
  showAutomation: previewModeActive = false,
}: TemplateListProps): JSX.Element | null => {
  if (isLoading && !hasNextPage) {
    return (
      <SelectionList.Section title={sectionTitle}>
        <LoadingBox
          loadingHeight={loadingHeight}
          loadingMessage={loadingMessage}
        />
      </SelectionList.Section>
    );
  }

  if (isError) {
    return (
      <SelectionList.Section title={sectionTitle}>
        <FlexBox height="auto" padding="300">
          <Text.Paragraph>{errorMessage}</Text.Paragraph>
        </FlexBox>
      </SelectionList.Section>
    );
  }

  if (templates.length === 0) {
    if (emptyMessage) {
      return (
        <FlexBox
          as="li"
          padding="0 300 300"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyState title={emptyMessage} description={emptyDescription} />
        </FlexBox>
      );
    }
    return null;
  }

  return (
    <>
      <SelectionList.Section title={sectionTitle}>
        {templates.map((template) => (
          <TemplateItem
            key={template.id}
            item={template}
            toggleFavorite={toggleFavorite}
            searchTerm={searchTerm}
            locale={locale}
            showAutomation={previewModeActive}
          />
        ))}
      </SelectionList.Section>
      {isLoading && hasNextPage && (
        <LoadingBox
          loadingHeight={loadingHeight}
          loadingMessage={loadingMessage}
        />
      )}
      {!isLoading && hasNextPage && <Waypoint onEnter={nextPage} />}
    </>
  );
};
